// src/components/gamification/DailyChallenge.jsx - ENHANCED VERSION with Multiple Rotating Challenges
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Calendar, 
  CheckCircle, 
  Clock, 
  Zap, 
  Brain, 
  Heart, 
  Target,
  Sparkles,
  Trophy,
  Plus,
  Eye,
  Smile,
  Users,
  BookOpen,
  Coffee,
  Sunrise,
  Moon,
  Star,
  Music,
  TreePine,
  Compass,
} from 'lucide-react';
import { useGamification } from '../../context/VibeGamificationContext';
import { usePrivacyAuth } from '../../context/PrivacyAuthContext';
import Button from '../shared/Button';

// DAILY CHALLENGES POOL - Rotates based on day of year
const DAILY_CHALLENGES = [
  {
    id: 'inner-voice-checkin',
    title: 'Inner Voice Check-In',
    description: 'Notice what your inner DJ is playing 3 times today',
    icon: Brain,
    color: 'from-purple-500 to-indigo-500',
    borderColor: 'border-purple-500/30',
    instructions: [
      'Set 3 alarms throughout your day',
      'When they go off, pause and listen to your thoughts',
      'Ask: "What\'s my inner voice saying right now?"',
      'Click check-in below to track your progress',
    ],
    checkIns: 3,
    xpReward: 25,
  },
  {
    id: 'gratitude-spotlight',
    title: 'Gratitude Spotlight',
    description: 'Find 3 specific things to appreciate about yourself today',
    icon: Heart,
    color: 'from-pink-500 to-rose-500',
    borderColor: 'border-pink-500/30',
    instructions: [
      'Look for moments when you handled something well',
      'Notice positive qualities you demonstrated',
      'Appreciate small wins and efforts you made',
      'Check in each time you spot something to appreciate',
    ],
    checkIns: 3,
    xpReward: 25,
  },
  {
    id: 'confidence-builder',
    title: 'Confidence Builder',
    description: 'Practice confident body language and self-talk 3 times',
    icon: Star,
    color: 'from-yellow-500 to-orange-500',
    borderColor: 'border-yellow-500/30',
    instructions: [
      'Stand tall, shoulders back, chin up for 30 seconds',
      'Say something encouraging to yourself',
      'Practice speaking with a clear, confident tone',
      'Check in after each confidence practice session',
    ],
    checkIns: 3,
    xpReward: 25,
  },
  {
    id: 'emotion-detective',
    title: 'Emotion Detective',
    description: 'Identify and name 3 different emotions you experience',
    icon: Eye,
    color: 'from-cyan-500 to-blue-500',
    borderColor: 'border-cyan-500/30',
    instructions: [
      'When you feel something, pause and identify it',
      'Name the emotion specifically (frustrated vs just "bad")',
      'Notice what triggered the emotion',
      'Check in each time you successfully identify an emotion',
    ],
    checkIns: 3,
    xpReward: 25,
  },
  {
    id: 'kindness-mission',
    title: 'Kindness Mission',
    description: 'Perform 3 acts of kindness - to others or yourself',
    icon: Users,
    color: 'from-green-500 to-emerald-500',
    borderColor: 'border-green-500/30',
    instructions: [
      'Compliment someone genuinely',
      'Help someone with something small',
      'Do something nice for yourself',
      'Check in after each act of kindness',
    ],
    checkIns: 3,
    xpReward: 25,
  },
  {
    id: 'mindful-moments',
    title: 'Mindful Moments',
    description: 'Take 3 mindful breathing breaks throughout the day',
    icon: TreePine,
    color: 'from-teal-500 to-cyan-500',
    borderColor: 'border-teal-500/30',
    instructions: [
      'Take 5 deep, slow breaths',
      'Focus only on the sensation of breathing',
      'Notice how your body feels',
      'Check in after each mindful breathing session',
    ],
    checkIns: 3,
    xpReward: 25,
  },
  {
    id: 'growth-spotter',
    title: 'Growth Spotter',
    description: 'Notice 3 ways you\'ve grown or improved recently',
    icon: Sparkles,
    color: 'from-indigo-500 to-purple-500',
    borderColor: 'border-indigo-500/30',
    instructions: [
      'Think about what you can do now that you couldn\'t before',
      'Notice positive changes in your thinking patterns',
      'Appreciate progress in any area of life',
      'Check in each time you recognise growth',
    ],
    checkIns: 3,
    xpReward: 25,
  },
  {
    id: 'energy-tracker',
    title: 'Energy Tracker',
    description: 'Check in on your energy levels 3 times and adjust accordingly',
    icon: Zap,
    color: 'from-amber-500 to-orange-500',
    borderColor: 'border-amber-500/30',
    instructions: [
      'Rate your energy from 1-10',
      'If low, do something to boost it (movement, music, etc.)',
      'If high, channel it into something productive',
      'Check in each time you assess and adjust your energy',
    ],
    checkIns: 3,
    xpReward: 25,
  },
  {
    id: 'future-self-advisor',
    title: 'Future Self Advisor',
    description: 'Ask your future self for guidance 3 times today',
    icon: Compass,
    color: 'from-blue-500 to-indigo-500',
    borderColor: 'border-blue-500/30',
    instructions: [
      'Before decisions, ask "What would future me choose?"',
      'Imagine yourself 5 years from now giving advice',
      'Consider what choice would make your future self proud',
      'Check in each time you consult your future self',
    ],
    checkIns: 3,
    xpReward: 25,
  },
  {
    id: 'strength-spotter',
    title: 'Strength Spotter',
    description: 'Identify 3 personal strengths you used today',
    icon: Trophy,
    color: 'from-purple-500 to-pink-500',
    borderColor: 'border-purple-500/30',
    instructions: [
      'Notice when you use creativity, persistence, kindness, etc.',
      'Look for strengths in how you handle challenges',
      'Appreciate unique talents and abilities you have',
      'Check in each time you spot a strength in action',
    ],
    checkIns: 3,
    xpReward: 25,
  },
];

const DailyChallenge = ({ className = '' }) => {
  const { 
    completedModules, 
    currentStreak, 
    addXP,
    helpSomeone,
    getCurrentUserId,
  } = useGamification();
  
  // Use PrivacyAuth for user authentication
  const { user } = usePrivacyAuth();
  
  const [challengeProgress, setChallengeProgress] = useState(0);
  const [isCompleted, setIsCompleted] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  const [todaysChallenge, setTodaysChallenge] = useState(null);
  
  // Get today's challenge based on day of year
  const getTodaysChallenge = () => {
    const today = new Date();
    const dayOfYear = Math.floor((today - new Date(today.getFullYear(), 0, 0)) / (1000 * 60 * 60 * 24));
    const challengeIndex = dayOfYear % DAILY_CHALLENGES.length;
    return DAILY_CHALLENGES[challengeIndex];
  };

  // Initialize today's challenge
  useEffect(() => {
    const challenge = getTodaysChallenge();
    setTodaysChallenge(challenge);
  }, []);

  const today = new Date().toDateString();
  
  // User-specific storage key
  const getStorageKey = () => {
    const userId = user?.userId;
    const dateKey = today.replace(/\s/g, '_');
    const challengeId = todaysChallenge?.id || 'default';
    return userId ? `daily_challenge_${dateKey}_${challengeId}_${userId}` : `daily_challenge_${dateKey}_${challengeId}`;
  };
  
  // Load today's challenge progress
  useEffect(() => {
    if (!todaysChallenge) return;
    
    const userId = user?.userId;
    
    if (!userId) {
      console.log('🚫 No user logged in, skipping daily challenge load');
      return;
    }

    const storageKey = getStorageKey();
    const savedProgress = localStorage.getItem(storageKey);
    
    if (savedProgress) {
      try {
        const progress = JSON.parse(savedProgress);
        setChallengeProgress(progress.checkIns || 0);
        setIsCompleted(progress.completed || false);
        console.log('📥 Loaded daily challenge progress for user:', userId, progress);
      } catch (error) {
        console.error('❌ Failed to load daily challenge progress:', error);
      }
    } else {
      console.log('📝 No daily challenge progress found for user:', userId);
      // Reset for new challenge
      setChallengeProgress(0);
      setIsCompleted(false);
    }
  }, [todaysChallenge, user?.userId]);

  // Save progress to localStorage with user ID
  const saveProgress = (checkIns, completed = false) => {
    const userId = user?.userId;
    
    if (!userId || !todaysChallenge) {
      console.log('🚫 No user logged in or challenge loaded, skipping save');
      return;
    }

    const progressData = {
      checkIns,
      completed,
      challengeId: todaysChallenge.id,
      userId,
      lastUpdated: new Date().toISOString(),
    };
    
    const storageKey = getStorageKey();
    localStorage.setItem(storageKey, JSON.stringify(progressData));
    
    setChallengeProgress(checkIns);
    setIsCompleted(completed);
    
    console.log('💾 Saved daily challenge progress for user:', userId, progressData);
  };

  const handleCheckIn = () => {
    const userId = user?.userId;
    
    if (!userId || !todaysChallenge) {
      console.log('🚫 No user logged in or challenge loaded, cannot complete check-in');
      return;
    }

    if (isCompleted || challengeProgress >= todaysChallenge.checkIns) return;
    
    const newProgress = challengeProgress + 1;
    const nowCompleted = newProgress >= todaysChallenge.checkIns;
    
    console.log(`📝 Daily challenge check-in ${newProgress}/${todaysChallenge.checkIns} for user:`, userId);
    
    // Award XP: Split total XP across check-ins
    const xpPerCheckIn = Math.floor(todaysChallenge.xpReward / todaysChallenge.checkIns);
    const finalCheckInBonus = todaysChallenge.xpReward - (xpPerCheckIn * (todaysChallenge.checkIns - 1));
    const xpForCheckIn = newProgress === todaysChallenge.checkIns ? finalCheckInBonus : xpPerCheckIn;
    
    addXP(xpForCheckIn, `${todaysChallenge.title} Check-in ${newProgress}/${todaysChallenge.checkIns}`);
    
    // Show completion celebration
    if (nowCompleted) {
      setShowCelebration(true);
      setTimeout(() => setShowCelebration(false), 3000);
    }
    
    saveProgress(newProgress, nowCompleted);
  };

  const resetChallenge = () => {
    if (!todaysChallenge) return;
    
    const storageKey = getStorageKey();
    localStorage.removeItem(storageKey);
    setChallengeProgress(0);
    setIsCompleted(false);
    console.log('🔄 Reset daily challenge for current user');
  };

  // Get streak multiplier bonus display
  const getStreakBonus = () => {
    if (currentStreak >= 7) return '🔥 Streak Bonus Active!';
    if (currentStreak >= 3) return '⚡ Building Momentum!';
    return null;
  };

  // Don't render if no user is logged in
  if (!user) {
    return (
      <motion.div
        className={`glassmorphism rounded-xl p-6 border border-white/10 ${className}`}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="text-center">
          <div className="text-4xl mb-2">🔐</div>
          <div className="text-white font-bold mb-2">Daily Challenge</div>
          <div className="text-slate-300 text-sm">Sign in to track your daily progress!</div>
        </div>
      </motion.div>
    );
  }

  // Don't render if challenge not loaded yet
  if (!todaysChallenge) {
    return (
      <motion.div
        className={`glassmorphism rounded-xl p-6 border border-white/10 ${className}`}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="text-center">
          <div className="text-4xl mb-2">⏳</div>
          <div className="text-white font-bold mb-2">Loading Today's Challenge...</div>
        </div>
      </motion.div>
    );
  }

  const IconComponent = todaysChallenge.icon;

  return (
    <motion.div
      className={`glassmorphism rounded-xl p-6 border border-white/10 ${className}`}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      {/* Celebration */}
      {showCelebration && (
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.8 }}
          className="absolute inset-0 bg-gradient-to-r from-yellow-400/20 to-orange-500/20 rounded-xl flex items-center justify-center z-10"
        >
          <div className="text-center">
            <div className="text-4xl mb-2">🎉</div>
            <div className="text-white font-bold">Challenge Complete!</div>
            <div className="text-yellow-300 text-sm">+{todaysChallenge.xpReward} XP Earned</div>
          </div>
        </motion.div>
      )}

      {/* ENHANCED HEADER with title and challenge info */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={`w-12 h-12 bg-gradient-to-r ${todaysChallenge.color} rounded-xl flex items-center justify-center`}>
            <IconComponent className="w-6 h-6 text-white" />
          </div>
          <div>
            <div className="flex items-center gap-2 mb-1">
              <h3 className="text-white font-bold text-lg">Daily Challenge</h3>
              {currentStreak >= 7 && (
                <span className="px-2 py-1 bg-purple-500/20 text-purple-400 text-xs rounded-full font-bold">
                  Streak Bonus!
                </span>
              )}
            </div>
            <h4 className="text-purple-200 font-semibold">{todaysChallenge.title}</h4>
            <p className="text-slate-300 text-sm">{todaysChallenge.description}</p>
          </div>
        </div>
        
        <div className="text-right">
          <div className="flex items-center gap-1 text-purple-400 font-bold">
            <Zap className="w-4 h-4" />
            +{todaysChallenge.xpReward} XP
          </div>
          <div className="text-xs text-slate-400">Daily Reward</div>
        </div>
      </div>

      {/* Instructions */}
      <div className={`p-3 bg-white/5 rounded-lg mb-4 border ${todaysChallenge.borderColor}`}>
        <div className="flex items-start gap-2">
          <Clock className="w-4 h-4 text-blue-400 mt-0.5" />
          <div>
            <div className="text-sm font-medium text-white mb-1">How to complete:</div>
            <ul className="text-sm text-slate-300 space-y-1">
              {todaysChallenge.instructions.map((instruction, index) => (
                <li key={index}>• {instruction}</li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Progress Display */}
      <div className="mb-4">
        <div className="flex justify-between text-sm text-purple-200 mb-2">
          <span>Today's Progress</span>
          <span>{challengeProgress}/{todaysChallenge.checkIns} Check-ins</span>
        </div>
        
        {/* Progress circles */}
        <div className="flex items-center gap-2 mb-3">
          {Array.from({ length: todaysChallenge.checkIns }, (_, index) => {
            const step = index + 1;
            return (
              <div key={step} className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-colors ${
                  challengeProgress >= step 
                    ? 'bg-green-500 text-white' 
                    : 'bg-slate-700 text-slate-400'
                }`}>
                  {challengeProgress >= step ? <CheckCircle className="w-4 h-4" /> : step}
                </div>
                {step < todaysChallenge.checkIns && (
                  <div className={`w-4 h-0.5 mx-1 ${challengeProgress > step ? 'bg-green-500' : 'bg-slate-700'}`} />
                )}
              </div>
            );
          })}
        </div>
        
        {/* Progress bar */}
        <div className="w-full bg-slate-700 rounded-full h-2">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${(challengeProgress / todaysChallenge.checkIns) * 100}%` }}
            transition={{ duration: 0.5 }}
            className={`h-full bg-gradient-to-r ${todaysChallenge.color} rounded-full`}
          />
        </div>
      </div>

      {/* Action button */}
      <div className="space-y-3">
        <Button
          onClick={handleCheckIn}
          disabled={isCompleted || challengeProgress >= todaysChallenge.checkIns}
          className={`w-full ${
            isCompleted 
              ? 'bg-green-600/50 cursor-not-allowed' 
              : `bg-gradient-to-r ${todaysChallenge.color} hover:opacity-80`
          }`}
        >
          {isCompleted ? (
            <>
              <CheckCircle className="w-4 h-4 mr-2" />
              Challenge Complete! 🎉
            </>
          ) : (
            <>
              <Plus className="w-4 h-4 mr-2" />
              Check-in {challengeProgress + 1}/{todaysChallenge.checkIns}
            </>
          )}
        </Button>

        {/* Streak info */}
        <div className="flex items-center justify-between text-sm">
          <span className="text-slate-400">Challenge Streak: {currentStreak} days</span>
          {getStreakBonus() && (
            <span className="text-orange-400 font-medium">{getStreakBonus()}</span>
          )}
        </div>
      </div>

      {/* Debug reset button (remove in production) */}
      {process.env.NODE_ENV === 'development' && (
        <button
          onClick={resetChallenge}
          className="mt-2 text-xs text-slate-500 hover:text-slate-400"
        >
          Reset (Debug)
        </button>
      )}
    </motion.div>
  );
};

export default DailyChallenge;